import { Button, Flex } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Role of the item being rendered inside a group, so callers can style the
 * grouped parent differently from a standalone item or a nested replacement.
 */
export type ReplacementRole = 'standalone' | 'parent' | 'replacement';

/**
 * Shared expandable "substitutivas" group used by both the Avaliações and
 * Atividades lists in the professor area. Renders `item`; when it has
 * replacements, wraps it in a bordered box with an expandable section listing
 * each substitutiva. The caller owns how each row looks via `renderItem`.
 */
interface ReplacementGroupProps<T extends { id: string; replacements?: T[] }> {
  item: T;
  renderItem: (item: T, role: ReplacementRole) => ReactNode;
}

export function ReplacementGroup<T extends { id: string; replacements?: T[] }>({
  item,
  renderItem
}: ReplacementGroupProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const replacements = item.replacements ?? [];

  if (replacements.length === 0) {
    return <>{renderItem(item, 'standalone')}</>;
  }

  return (
    <Flex
      vertical
      className="my-2 border border-neutral-200 rounded-lg overflow-hidden"
    >
      {renderItem(item, 'parent')}

      <Button
        type="link"
        size="small"
        icon={expanded ? <DownOutlined /> : <RightOutlined />}
        onClick={() => setExpanded(prev => !prev)}
        className="self-start !px-2"
      >
        {replacements.length}{' '}
        {replacements.length === 1 ? 'substitutiva' : 'substitutivas'}
      </Button>

      {expanded && (
        <Flex
          vertical
          className="pl-10 border-t border-neutral-100 bg-neutral-50"
        >
          {replacements.map(replacement => (
            <Fragment key={replacement.id}>
              {renderItem(replacement, 'replacement')}
            </Fragment>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
